import { z } from 'zod'
import { createFileRoute } from '@tanstack/react-router'
import { featuredProductsInfiniteOptions } from '@/hooks/queries/featured/products'
import { followingFeedInfiniteOptions } from '@/hooks/queries/feeds'
import { userCollectionsQueryOptions } from '@/hooks/queries/userCollectionsQueryOptions'
import { DiscoverFeed } from '@/components/feeds/discover/DiscoverFeed'
import { StickyHeader } from '@/components/shared/StickyHeader'
import { FeedbackButton } from '@/components/feedback/FeedbackButton'
// import { ForYouFeed } from '@/components/feeds/forYou/ForYouFeed'
import { FollowingFeed } from '@/components/feeds/following/FollowingFeed'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useFeedSeed } from '@/hooks/useFeedSeed'
import { TryOnInfoModal } from '@/components/try-on/TryOnInfoModal'
import { ProductFiltersDropdown } from '@/components/filters/ProductFiltersDropdown'
import { ProductFiltersDrawer } from '@/components/filters/ProductFiltersDrawer'
import { FollowingFeedFiltersDrawer } from '@/components/filters/FollowingFeedFiltersDrawer'
import { useProductFilters } from '@/stores/productFilters'
import { useFollowingFeedFilters } from '@/stores/followingFeedFilters'

type HomeTabValue = 'discover' | 'following'

const homeSearchSchema = z.object({
    tab: z.enum(['discover', 'following']).default('following').catch('following'),
})

export const Route = createFileRoute('/')({
    component: RouteComponent,
    validateSearch: homeSearchSchema,
    // Keep home page data fresh for longer since it's frequently accessed
    preloadStaleTime: 15_000, // 15 seconds - longer than router default
    loader: async ({ context: { queryClient } }) => {
        // Non-blocking prefetch - don't await, just kick off the query
        // This allows the page to render immediately while data loads in background
        queryClient.prefetchInfiniteQuery(
            featuredProductsInfiniteOptions(queryClient)
        )

        // Don't await - let the page render while data loads
        // DiscoverFeed will show loading state until data arrives
    },
})

function RouteComponent() {
    const { tab: activeTab } = Route.useSearch()
    const navigate = Route.useNavigate()
    const { user, isAuthenticated, loading: userLoading } = useCurrentUser()
    const queryClient = useQueryClient()

    const [isTryOnModalOpen, setIsTryOnModalOpen] = useState(false)

    // Filter UI state
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false)
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
    const [isFollowingFilterDrawerOpen, setIsFollowingFilterDrawerOpen] = useState(false)
    const filterButtonRef = useRef<HTMLButtonElement>(null)
    const { filterParams } = useProductFilters()
    const { gender: followingFeedGender } = useFollowingFeedFilters()

    // Desktop-only: Prefetch user collections for faster AddToCollectionPanel loading
    useEffect(() => {
        if (isAuthenticated && user?.uid && typeof window !== 'undefined') {
            const isDesktop = window.matchMedia('(min-width: 768px)').matches
            if (isDesktop) {
                // Non-blocking prefetch - don't await
                queryClient.prefetchQuery(
                    userCollectionsQueryOptions(queryClient, user.uid)
                )
            }
        }
    }, [isAuthenticated, user?.uid, queryClient])

    // Feed seed management for following feed
    const { seed: followingFeedSeed, resetSeed: resetFollowingFeedSeed, updateSeed: updateFollowingFeedSeed } = useFeedSeed()

    // Get following feed data - fetch for both authenticated users and guests (for guest feed)
    const {
        data: followingFeedData,
        hasNextPage: hasNextFollowingPage,
        isFetchingNextPage: isFetchingNextFollowingPage,
        fetchNextPage: fetchNextFollowingPage,
        isLoading: followingFeedLoading,
    } = useInfiniteQuery({
        ...followingFeedInfiniteOptions(queryClient, { seed: followingFeedSeed }),
        enabled: !userLoading && activeTab === 'following',
    })

    // Extract seed from first page response and update it
    useEffect(() => {
        if (followingFeedData?.pages?.[0]?.seed) {
            updateFollowingFeedSeed(followingFeedData.pages[0].seed)
        }
    }, [followingFeedData?.pages?.[0]?.seed, updateFollowingFeedSeed])

    // Memoize the feed items to prevent unnecessary re-renders from new array references
    const followingFeedItems = useMemo(
        () => followingFeedData?.pages?.flatMap((page) => page.items) ?? [],
        [followingFeedData?.pages]
    )

    // Memoize the refresh handler to prevent unnecessary re-renders
    const handleFollowingFeedRefresh = useCallback(async () => {
        await queryClient.cancelQueries({
            queryKey: ['feeds', 'following'],
            exact: false,
        })
        // Reset seed to get new content - this will automatically trigger a new query
        // since the seed is part of the query key
        resetFollowingFeedSeed()
        // Reset only the following infinite feed queries to avoid stale re-population
        await queryClient.resetQueries({
            queryKey: ['feeds', 'following', 'infinite'],
            exact: false,
        })
    }, [queryClient, resetFollowingFeedSeed])

    // Memoize tab change handler
    const setActiveTab = useCallback((tab: HomeTabValue) => {
        if (tab === 'following' && activeTab === 'following') {
            // User clicked Following tab while already on Following - refresh feed
            handleFollowingFeedRefresh()
        } else {
            // Normal navigation
            navigate({ search: { tab } })
        }
    }, [activeTab, handleFollowingFeedRefresh, navigate])

    // Memoize filter button click handlers
    const handleFilterButtonClick = useCallback(() => {
        if (typeof window !== 'undefined') {
            const isDesktop = window.matchMedia('(min-width: 768px)').matches
            if (isDesktop) {
                setIsFilterDropdownOpen(prev => !prev)
            } else {
                setIsFilterDrawerOpen(true)
            }
        }
    }, [])

    const handleFollowingFilterButtonClick = useCallback(() => {
        setIsFollowingFilterDrawerOpen(true)
    }, [])

    const handleTryOnClick = useCallback(() => {
        setIsTryOnModalOpen(true)
    }, [])


    return (
        <section className="mx-auto flex max-w-md flex-col md:max-w-full md:px-8 md:py-0">
            {/* Unified top bar with filters and tabs - sticky with scroll hide/show behavior on mobile */}
            <StickyHeader
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onFilterButtonClick={handleFilterButtonClick}
                onFollowingFilterButtonClick={handleFollowingFilterButtonClick}
                onTryOnClick={handleTryOnClick}
                filterButtonRef={filterButtonRef}
            />

            <div className="relative">
                {/* Conditionally render tab content for better performance */}
                {activeTab === 'discover' && (
                    <div className="w-full touch-pan-y">
                        <div className="px-2">
                            <DiscoverFeed params={filterParams} />
                        </div>
                    </div>
                )}

                {activeTab === 'following' && (
                    <div className="w-full touch-pan-y">
                        <FollowingFeed
                            feedItems={followingFeedItems}
                            data={followingFeedData}
                            isLoading={followingFeedLoading}
                            hasNextPage={hasNextFollowingPage}
                            isFetchingNextPage={isFetchingNextFollowingPage}
                            fetchNextPage={fetchNextFollowingPage}
                            genderFilter={followingFeedGender}
                            isGuestFeed={followingFeedData?.pages?.[0]?.isGuestFeed ?? false}
                        />
                    </div>
                )}
            </div>



            <div className="fixed">
                {/* Try On Info Modal */}
                <TryOnInfoModal
                    open={isTryOnModalOpen}
                    onOpenChange={setIsTryOnModalOpen}
                />
            </div>

            {/* Filter UI - Desktop: Dropdown, Mobile: Drawer */}
            <ProductFiltersDropdown
                isOpen={isFilterDropdownOpen}
                onClose={() => setIsFilterDropdownOpen(false)}
                anchorEl={filterButtonRef.current}
            />
            <section className="fixed">
                {/* Feedback Bubble Overlays for Comparison */}
                {/* Feedback button overlay */}
                <FeedbackButton />
                <ProductFiltersDrawer
                    isOpen={isFilterDrawerOpen}
                    onClose={() => setIsFilterDrawerOpen(false)}
                />
                <FollowingFeedFiltersDrawer
                    isOpen={isFollowingFilterDrawerOpen}
                    onClose={() => setIsFollowingFilterDrawerOpen(false)}
                />
            </section>
        </section >
    )
}
